/**
 * Helpers for navigating between the Product list (tabbed) and its detail /
 * edit / import pages while keeping the user on the tab they came from.
 *
 * The Product list keeps its state in the URL (tab, page, pageSize, search,
 * sort). Every page opened from a tab must carry that search string forward so
 * its "Back" / "Close" action can return to the exact same tab and state.
 */

export const PRODUCT_TABS = {
  INCOMPLETE: "incomplete",
  PENDING_QC: "pending-qc",
  LIVE: "live",
  BULK_UPDATE: "bulk-update",
};

export const DEFAULT_PRODUCT_TAB = PRODUCT_TABS.INCOMPLETE;

/**
 * Returns the current list search string with `tab` forced to the given value.
 * @param {string} search current location.search (may be empty)
 * @param {string} tab tab key to return to
 * @returns {string} search string starting with "?" (or "" when empty)
 */
export const buildProductListSearch = (search, tab) => {
  const params = new URLSearchParams(search || "");

  if (tab && tab !== DEFAULT_PRODUCT_TAB) {
    params.set("tab", tab);
  } else {
    // The list falls back to the default tab when `tab` is absent, so keeping
    // the param out avoids a redundant entry in the URL.
    params.delete("tab");
  }

  const query = params.toString();
  return query ? `?${query}` : "";
};

/**
 * Resolves the tab a detail page should return to, based on its own URL.
 * @param {string} search current location.search
 * @returns {string} tab key
 */
export const getProductTabFromSearch = (search) =>
  new URLSearchParams(search || "").get("tab") || DEFAULT_PRODUCT_TAB;
